/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useState } from "react";
import { useTranslation } from "@i18n";
import { type TranslatedString, untranslated } from "@Helpers/types/i18n";
import { defaultModulesMapper } from "@Helpers/constants";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { SpacesDataResponse } from "@ServerTypes/spaces/space.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** One module's entry inside a space user's `modulePermissions` bag, as this picker reads it. */
export type SpaceModulePermission = { role?: number; canCreate?: boolean; permissions?: number[] };

export interface Space {
  /**
   * Branded: `spacesData` returns `spaces._id` as a 24-hex ObjectId string, and the consumers of
   * `selectedSpaceCallback` annotate their handler parameter as `{ _id: ObjectIdString }`. Declaring
   * it as a plain `string` here is what made those handlers unassignable.
   */
  _id: ObjectIdString;
  id?: ObjectIdString;
  spaceName: string;
  isDefault?: boolean;
  /**
   * The calling user's own permission bag; only `canCreate` is read here. OPTIONAL, matching
   * `spaceDataItemSchema`: spacesData projects it with `$arrayElemAt`, which emits MISSING when the
   * caller is not a member of the space.
   */
  modulePermissions?: Record<string, SpaceModulePermission | undefined>;
}

/**
 * `spacesDataResponse.result` (packages/server/spaces/space.schema.ts) is typed with
 * `spacesDeleteResult` — `{ deletedSpaceName?; spaceName? }`, the shape of the action="remove" branch
 * only. The list request this hook issues returns `spaceDataItemSchema[]` instead; the
 * `spacesDataResult` union declared just above it in that file already spells both shapes out, but
 * the response schema does not use it. Until it does, the list shape is recovered in this one place.
 */
function spaceListOf(result: SpacesDataResponse["result"]): Space[] | undefined {
  return Array.isArray(result) ? (result as Space[]) : undefined;
}

interface UseSelectSpacesProps {
  /** Widened to accept `undefined`; see the note on `SelectSpacesProps.projectId`. */
  projectId: string | undefined;
  workspaceId?: string;
  /**
   * METHOD SHORTHAND, deliberately: the 13 call sites annotate this handler with their own narrower
   * parameter shapes (`{ _id: ObjectIdString }`, `{ _id?: string }`, `Space`, …). Method syntax makes
   * the parameter bivariant, which accepts all of them; a property arrow is contravariant and rejects
   * every one. Both parameters must also stay NON-NULL and NON-OPTIONAL — a `| null` or `?` here
   * defeats bivariance in both directions — so the nullability is pushed to the call sites below.
   */
  selectedSpaceCallback(space: Space, spaceData: Space[]): void;
  fetchSpaceListCallback?: (response: { data: SpacesDataResponse }) => void;
  dataSource?: Space[] | null;
  disablePermissionCheck?: boolean;
  moduleName?: keyof typeof defaultModulesMapper;
}

function useSelectSpaces({
  projectId,
  workspaceId,
  selectedSpaceCallback,
  fetchSpaceListCallback,
  dataSource,
  disablePermissionCheck,
  moduleName = "",
}: UseSelectSpacesProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [spaceData, setSpaceData] = useState<Space[] | null>(dataSource ?? null);
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  const [cannotCreateInAnySpace, setCannotCreateInAnySpace] = useState<boolean>(false);
  const [switchSpaceId, setSwitchSpaceId] = useState<string | undefined>(workspaceId);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (workspaceId) {
      setSwitchSpaceId(workspaceId);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchSpaceList();
  }, [projectId, dataSource]);

  useEffect(() => {
    if (cannotCreateInAnySpace && spaceData?.length) {
      dispatch(
        showToastNotification({
          content: t("You do not have create permission for this entity in any space within this project."),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  }, [cannotCreateInAnySpace, spaceData?.length]);

  useEffect(() => {
    if (!selectedSpace && spaceData && switchSpaceId) {
      const space = spaceData.find((item) => item._id === switchSpaceId || item.id === switchSpaceId) ?? null;
      if (space) {
        setSelectedSpace(space);
        selectedSpaceCallback(space, spaceData!);
      }
    }
  }, [spaceData, switchSpaceId]);

  // `defaultModulesMapper` is typed `any` in @Helpers/constants, so `keyof typeof` degrades to
  // `string | number | symbol` and cannot index the string-keyed `modulePermissions` bag. Every value
  // callers pass is one of that constant's string members, so normalise rather than assert.
  const modulePermissionKey = String(moduleName);

  const spaceSelectCheck = (item: Space | undefined) => {
    return disablePermissionCheck || item?.modulePermissions?.[modulePermissionKey]?.canCreate;
  };

  async function fetchSpaceList() {
    if (dataSource) {
      setSpaceData(dataSource);
      if (!switchSpaceId && Array.isArray(dataSource) && dataSource.length > 0) {
        const defaultSpace = dataSource.find((item) => item.isDefault);
        const firstSpace = dataSource[0];
        const initialSpaceId = defaultSpace?._id ?? firstSpace?.id;
        setSwitchSpaceId(initialSpaceId);
      }
    } else {
      setIsLoading(true);
      try {
        const response = await axiosWrapper.post<SpacesDataResponse>(`${getBackendUrl()}/spaces/spacesData`, {
          value: {
            projectId,
          },
        });
        fetchSpaceListCallback?.(response);
        const resultArr = spaceListOf(response.data.result);
        if (!switchSpaceId) {
          const defaultSpace = resultArr?.find((item) => item.isDefault && spaceSelectCheck(item));

          const initialSpaceId = defaultSpace?._id ?? resultArr?.find((item) => spaceSelectCheck(item))?._id;

          if (initialSpaceId) {
            setSwitchSpaceId(initialSpaceId);
          } else {
            setCannotCreateInAnySpace(true);
          }
        }
        setSpaceData(resultArr ?? []);
      } catch (error) {
        console.error("Error fetching spaces:", error);
        setSpaceData([]);
        Promise.reject(error);
      } finally {
        setIsLoading(false);
      }
    }
  }

  const selectSpace = (space: Space) => {
    if (spaceSelectCheck(space)) {
      setSwitchSpaceId(space._id);
      selectedSpaceCallback(space, spaceData!);
      setSelectedSpace(space);
    } else {
      dispatch(
        showToastNotification({
          content: t("You do not have create permission for this entity in this space"),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  };

  const openSelector = () => {
    setIsOpen(true);
  };

  const closeSelector = () => {
    setIsOpen(false);
  };

  const getSpaceDisplayText = (): TranslatedString => {
    if (!selectedSpace) return untranslated("");
    return selectedSpace.isDefault
      ? untranslated(`${selectedSpace.spaceName} (${t("Default")})`)
      : untranslated(selectedSpace.spaceName);
  };

  const isSpaceSelected = (space: Space) => {
    const spaceId = space._id || space.id;
    return spaceId === switchSpaceId;
  };

  const hasSpaces = spaceData !== null && spaceData.length > 0;

  const noSpacesAvailable = spaceData !== null && spaceData.length === 0;

  return {
    // State
    spaceData,
    selectedSpace,
    switchSpaceId,
    isLoading,
    isOpen,
    cannotCreateInAnySpace,

    // Actions
    selectSpace,
    openSelector,
    closeSelector,
    fetchSpaceList,

    // Computed
    getSpaceDisplayText,
    isSpaceSelected,
    hasSpaces,
    noSpacesAvailable,
  };
}

export default useSelectSpaces;
